/* Import Partners Map
 *
 * <import-partners-map> drives the coverage map's sticky cards: hovering a
 * region opens that region's card, which stays open (so its links are
 * clickable) until another region is hovered, the user clicks outside the
 * map, or presses Escape. Cards are server-rendered by the section; this
 * component only toggles and positions them.
 *
 * Clicking (or Enter/Space on) a region jumps to that region's band in the
 * Import Partners Regions section below (#<region>-partners), per client
 * request 2026-08-26 (Basecamp todo 9439755355). Regions with no band on the
 * page fall back to toggling their card. Below 750px taps jump too
 * (2026-08-27); openCard() is a no-op there, so nothing else happens.
 */
class ImportPartnersMap extends HTMLElement {
  constructor() {
    super();

    this.stage = this.querySelector('.import-partners-map__stage');
    this.svg = this.querySelector('.import-partners-map__svg');
    if (!this.stage || !this.svg) return;

    this.openRegion = null;

    // Hover: delegate pointerover so we know which country path the pointer
    // is on (enter events don't bubble). Only reacts when the region changes,
    // which is what keeps the open card sticky.
    this.svg.addEventListener('pointerover', (event) => {
      if (event.pointerType === 'touch') return;
      const region = event.target.closest('.import-partners-map__region');
      if (!region) return;
      const key = region.dataset.region;
      if (key !== this.openRegion) this.openCard(key, region, event.target);
    });

    // Click / tap on a region: jump to its details band below. Falls back to
    // toggling the card for regions that have no band on the page.
    this.svg.addEventListener('click', (event) => {
      const region = event.target.closest('.import-partners-map__region');
      if (!region) return;
      const key = region.dataset.region;
      if (this.jumpToRegion(key)) return;
      if (key === this.openRegion) {
        this.closeCard();
      } else {
        this.openCard(key, region, event.target);
      }
    });

    // Keyboard: regions are focusable buttons; Enter/Space toggles.
    this.svg.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const region = event.target.closest('.import-partners-map__region');
      if (!region) return;
      event.preventDefault();
      const key = region.dataset.region;
      if (this.jumpToRegion(key)) return;
      if (key === this.openRegion) {
        this.closeCard();
      } else {
        this.openCard(key, region, region);
      }
    });

    // Outside click closes; clicks inside the map or an open card don't.
    document.addEventListener('click', (event) => {
      if (!this.openRegion) return;
      if (this.contains(event.target)) return;
      this.closeCard();
    });

    // Escape closes from anywhere on the page.
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.closeCard();
    });
  }

  /* Show the card for a region key, positioned near the hovered country.
   * Below 750px there are no cards (taps only jump to the region band), so
   * this is a no-op; the guard also covers keyboard focus reaching a region. */
  openCard(key, region, target) {
    if (!window.matchMedia('(min-width: 750px)').matches) return;
    const card = this.querySelector(`.import-partners-map__card[data-region="${key}"]`);
    if (!card) return;

    this.closeCard();
    card.hidden = false;
    region.setAttribute('aria-expanded', 'true');
    region.classList.add('is-active');
    this.openRegion = key;
    this.positionCard(card, target && target.getBBox ? target : region);
  }

  /* Scroll to the region's band in the regions section (id "<key>-partners").
   * Sets the hash so the jump behaves like the cards' "See partner details"
   * links and lands in browser history; re-clicking the same region (hash
   * unchanged, so no native scroll) scrolls explicitly. Moves focus to the
   * band so keyboard users land where they jumped. Returns false when the
   * page has no band for the region. */
  jumpToRegion(key) {
    const id = `${key}-partners`;
    const band = document.getElementById(id);
    if (!band) return false;

    this.closeCard();
    if (location.hash === `#${id}`) {
      band.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      location.hash = id;
    }
    band.focus({ preventScroll: true });
    return true;
  }

  closeCard() {
    if (!this.openRegion) return;
    const card = this.querySelector(`.import-partners-map__card[data-region="${this.openRegion}"]`);
    if (card) card.hidden = true;
    this.querySelectorAll('.import-partners-map__region').forEach((region) => {
      region.setAttribute('aria-expanded', 'false');
      region.classList.remove('is-active');
    });
    this.openRegion = null;
  }

  /* Place the card beside the anchor element's bounding box, clamped to the
   * stage. Uses the hovered country path (not the whole group) so disjoint
   * groups like "other" anchor near the pointer, not their global extent. */
  positionCard(card, anchor) {
    const stageRect = this.stage.getBoundingClientRect();
    const svgRect = this.svg.getBoundingClientRect();
    const viewBox = this.svg.viewBox.baseVal;
    const scaleX = svgRect.width / viewBox.width;
    const scaleY = svgRect.height / viewBox.height;

    const bbox = anchor.getBBox();
    const anchorLeft = svgRect.left - stageRect.left + bbox.x * scaleX;
    const anchorTop = svgRect.top - stageRect.top + bbox.y * scaleY;
    const anchorW = bbox.width * scaleX;
    const anchorH = bbox.height * scaleY;

    const gap = 12;
    const cardW = card.offsetWidth;
    const cardH = card.offsetHeight;

    // Prefer the right side of the region; flip left when it would overflow.
    let left = anchorLeft + anchorW + gap;
    if (left + cardW > stageRect.width) left = anchorLeft - cardW - gap;
    left = Math.max(gap, Math.min(left, stageRect.width - cardW - gap));

    let top = anchorTop + anchorH / 2 - cardH / 2;
    top = Math.max(gap, Math.min(top, stageRect.height - cardH - gap));

    card.style.left = `${Math.round(left)}px`;
    card.style.top = `${Math.round(top)}px`;
  }
}

customElements.define('import-partners-map', ImportPartnersMap);
